import { memo } from 'react';
import { CheckSquare, Clock, Repeat } from 'lucide-react';
import type { BoardTemplateSummary } from '@kaba/shared';
import { columnLook, cx, LABEL_BG } from '../ui/ui';

type Preview = BoardTemplateSummary['preview'];

/** Kleine Boardskizze für die Kacheln: Spalten als Streifen, Karten als Balken in der Vorlagenfarbe */
export const MiniPreview = memo(function MiniPreview({ preview, color }: { preview: Preview; color: string | null }) {
  const accent = color ?? 'var(--color-accent)';
  const cols = preview.columns.slice(0, 5);
  return (
    <div
      aria-hidden
      className="flex h-28 gap-1.5 overflow-hidden rounded-t-xl p-2.5"
      style={{ background: `color-mix(in oklab, ${accent} 14%, var(--panel))` }}
    >
      {cols.map((c, i) => (
        <div key={i} className="flex min-w-0 flex-1 flex-col gap-1 rounded-md bg-[var(--panel)]/70 p-1 dark:bg-black/20">
          <div className="h-1.5 w-3/4 rounded-full" style={{ background: accent, opacity: 0.7 }} />
          {c.cards.slice(0, 3).map((card, j) => (
            <div key={j} className="flex flex-col gap-0.5 rounded-[3px] bg-[var(--panel)] p-1 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
              {card.labels[0] && <div className={cx('h-1 w-3 rounded-full', LABEL_BG[card.labels[0].color])} />}
              <div className="h-1 w-full rounded-full bg-[var(--ink-soft)]/25" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
});

/** Große Vorschau auf der Detailseite und im Import: Spalten mit Beispielkarten oder als Liste */
export function BoardPreview({ preview, mode, empty }: { preview: Preview; mode: 'board' | 'list'; empty?: string }) {
  const hasCards = preview.columns.some((c) => c.cards.length);
  if (mode === 'list')
    return (
      <div className="flex flex-col gap-3">
        {preview.columns.map((c, i) => (
          <section key={i} aria-label={c.title} className="rounded-xl border border-[var(--line)] bg-[var(--panel)]">
            <div className="flex items-center gap-2 border-b border-[var(--line)] px-3 py-2 text-sm font-semibold">
              {c.title}
              <span className="text-xs font-normal text-[var(--ink-soft)]">{c.total ?? c.cards.length}</span>
            </div>
            {c.cards.length ? (
              <ul className="divide-y divide-[var(--line)]">
                {c.cards.map((card, j) => (
                  <li key={j} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <span className={cx('min-w-0 flex-1 truncate', card.done && 'text-[var(--ink-soft)] line-through')}>{card.title}</span>
                    <CardMeta card={card} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-3 py-2 text-xs text-[var(--ink-soft)]">Keine Beispielkarten</p>
            )}
          </section>
        ))}
      </div>
    );
  return (
    <div className="flex min-h-0 gap-3 overflow-x-auto pb-2 outline-none focus-visible:ring-2 focus-visible:ring-accent" role="list" aria-label="Spalten der Vorschau" tabIndex={0}>
      {preview.columns.map((c, i) => (
        <div
          key={i}
          role="listitem"
          aria-label={c.title}
          className={cx('flex max-w-60 min-w-[9.5rem] flex-1 basis-0 flex-col gap-2 rounded-xl p-2', columnLook(c.color).className)}
          style={columnLook(c.color).style}
        >
          <div className="flex items-center gap-1.5 px-1 pt-0.5 text-sm font-semibold">
            <span className="min-w-0 flex-1 truncate">{c.title}</span>
            {c.wipLimit != null && (
              <span className="rounded-full bg-black/5 px-1.5 text-[11px] font-medium text-[var(--ink-soft)] dark:bg-white/10" title="Begrenzung (WIP-Limit)">
                max. {c.wipLimit}
              </span>
            )}
            <span className="text-xs font-normal text-[var(--ink-soft)]">{c.total ?? c.cards.length}</span>
          </div>
          {(c.entryRule || c.exitRule) && (
            <p className="px-1 text-[11px] leading-snug text-[var(--ink-soft)]">
              {c.entryRule && <>Rein: {c.entryRule}</>}
              {c.entryRule && c.exitRule && <br />}
              {c.exitRule && <>Raus: {c.exitRule}</>}
            </p>
          )}
          {c.cards.map((card, j) => (
            <div key={j} className="flex flex-col gap-1.5 rounded-lg border border-[var(--line)] bg-[var(--panel)] p-2 text-sm shadow-sm">
              {card.labels.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {card.labels.map((l, k) => (
                    <span key={k} className={cx('inline-block h-2 w-6 rounded-full', LABEL_BG[l.color])} title={l.name || undefined} />
                  ))}
                </div>
              )}
              <span className={cx('leading-snug [overflow-wrap:anywhere]', card.done && 'text-[var(--ink-soft)] line-through')}>{card.title}</span>
              <CardMeta card={card} />
            </div>
          ))}
          {(c.total ?? 0) > c.cards.length && <p className="px-1 text-xs text-[var(--ink-soft)]">+ {(c.total ?? 0) - c.cards.length} weitere</p>}
          {!hasCards && i === 0 && empty && <p className="px-1 text-xs text-[var(--ink-soft)]">{empty}</p>}
        </div>
      ))}
    </div>
  );
}

function CardMeta({ card }: { card: Preview['columns'][number]['cards'][number] }) {
  if (!card.checklist && !card.due && !card.recurring) return null;
  return (
    <span className="flex shrink-0 items-center gap-2 text-xs text-[var(--ink-soft)]">
      {card.due && (
        <span className="inline-flex items-center gap-0.5" title="Mit Frist">
          <Clock size={12} aria-hidden />
          <span className="sr-only">Frist</span>
        </span>
      )}
      {card.recurring && (
        <span className="inline-flex items-center gap-0.5" title="Wiederkehrend">
          <Repeat size={12} aria-hidden />
          <span className="sr-only">wiederkehrend</span>
        </span>
      )}
      {card.checklist > 0 && (
        <span className="inline-flex items-center gap-0.5" title="Checkliste">
          <CheckSquare size={12} aria-hidden />
          {card.checklist}
        </span>
      )}
    </span>
  );
}
